class ScrollingCards {
  static initAll(scope = document) {
    scope.querySelectorAll('[data-scrolling-cards]').forEach((root) => {
      if (root.scrollingCards) {
        root.scrollingCards.layout();
        return;
      }
      root.scrollingCards = new ScrollingCards(root);
    });
  }

  constructor(root) {
    this.root = root;
    this.stack = root.querySelector('.js-card-scroll__stack-area');
    this.cards = [...root.querySelectorAll('.js-card-scroll__card')];
    if (!this.stack || this.cards.length < 2) return;

    this.count = this.cards.length;
    this.cardTop = 20;
    this.scrollStep = 1;
    this.slideFactor = 0.35;
    this.cardHeights = [];
    this.states = this.cards.map(() => ({ opacity: 1, translateY: 0 }));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.enterObserver = null;
    this.ready = false;
    this.resizeTimer = 0;
    this.raf = 0;

    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.layout(), 120);
    };
    this.onScroll = () => this.scheduleVisualUpdate();
    this.onWheel = () => this.scheduleVisualUpdate();

    this.observeEnter();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('wheel', this.onWheel, { passive: true });
  }

  scheduleVisualUpdate() {
    if (this.raf) return;
    this.raf = window.requestAnimationFrame(() => {
      this.raf = 0;
      this.updateVisuals();
    });
  }

  observeEnter() {
    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      this.boot();
      return;
    }
    this.enterObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.boot();
      },
      { rootMargin: '160px 0px', threshold: 0 }
    );
    this.enterObserver.observe(this.root);
  }

  boot() {
    if (this.ready) return;
    this.ready = true;
    this.enterObserver?.disconnect();
    this.enterObserver = null;
    this.root.classList.add('is-ready');
    this.layout();
    this.scheduleVisualUpdate();
  }

  getCardTop() {
    const raw = getComputedStyle(this.root).getPropertyValue('--stack-card-top');
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 20;
  }

  getScrollStep() {
    const parsed = parseFloat(this.root.dataset.scrollStep);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  /** Scroll runway per card — tied to cardTop, not viewport height */
  getScrollRunway() {
    return Math.max(Math.round(this.cardTop * this.scrollStep), 40);
  }

  measureCardHeights() {
    this.cardHeights = this.cards.map((card) => card.getBoundingClientRect().height || card.offsetHeight);
  }

  isAtAnchor(index) {
    return this.cards[index].getBoundingClientRect().top <= this.cardTop + 2;
  }

  getCardRiseProgress(incomingIndex) {
    if (incomingIndex <= 0) return 0;

    const prevRect = this.cards[incomingIndex - 1].getBoundingClientRect();
    const nextTop = this.cards[incomingIndex].getBoundingClientRect().top;
    const riseEnd = this.cardTop;
    const riseStart = prevRect.bottom;

    if (riseStart <= riseEnd) return nextTop <= riseEnd ? 1 : 0;
    if (nextTop >= riseStart) return 0;
    if (nextTop <= riseEnd) return 1;

    return 1 - (nextTop - riseEnd) / (riseStart - riseEnd);
  }

  getTopCardIndex() {
    let top = 0;
    for (let j = 1; j < this.count; j++) {
      if (this.isAtAnchor(j)) top = j;
    }
    return top;
  }

  getCardOpacity(index) {
    if (index >= this.count - 1) return 1;
    if (!this.isAtAnchor(index)) return 1;

    const nextRise = this.getCardRiseProgress(index + 1);
    if (nextRise <= 0) return 1;

    return Math.max(0, 1 - nextRise);
  }

  getCardTranslateY(index) {
    const slide = this.cardTop * this.slideFactor;

    if (index === 0) {
      const cover = this.getCardRiseProgress(1);
      return cover > 0 ? cover * slide : 0;
    }

    let translateY = 0;
    const rise = this.getCardRiseProgress(index);

    if (rise > 0) {
      translateY += (1 - rise) * slide;
    }

    if (index < this.count - 1) {
      translateY += this.getCardRiseProgress(index + 1) * slide;
    }

    return translateY;
  }

  applyCardVisual(card, state) {
    const surface = card.querySelector('.js-card-scroll__card-surface');
    const targetEl = surface || card;

    targetEl.style.opacity = String(state.opacity);

    if (Math.abs(state.translateY) > 0.01) {
      targetEl.style.transform = `translate3d(0, ${state.translateY}px, 0)`;
    } else {
      targetEl.style.removeProperty('transform');
    }
  }

  updateVisuals() {
    if (!this.ready) return;

    const topIndex = this.getTopCardIndex();

    this.cards.forEach((card, index) => {
      const opacity = this.reducedMotion ? 1 : this.getCardOpacity(index);
      const translateY = this.reducedMotion ? 0 : this.getCardTranslateY(index);
      const state = this.states[index];

      state.opacity = opacity;
      state.translateY = translateY;

      this.applyCardVisual(card, state);
      card.style.pointerEvents = index === topIndex ? 'auto' : 'none';
    });
  }

  resetLayout() {
    this.stack.style.removeProperty('padding-bottom');
    this.cards.forEach((card, index) => {
      card.classList.remove('is-layout-ready');
      card.style.removeProperty('position');
      card.style.removeProperty('top');
      card.style.removeProperty('z-index');
      card.style.removeProperty('height');
      card.style.removeProperty('padding-bottom');
      card.style.removeProperty('overflow');
      card.style.removeProperty('box-sizing');
      card.style.removeProperty('transform');
      card.style.removeProperty('pointer-events');

      const surface = card.querySelector('.js-card-scroll__card-surface');
      if (!surface) return;
      surface.style.removeProperty('position');
      surface.style.removeProperty('inset');
      surface.style.removeProperty('height');
      surface.style.removeProperty('overflow');
      surface.style.removeProperty('transform');
      surface.style.removeProperty('opacity');

      this.states[index] = { opacity: 1, translateY: 0 };
    });
  }

  layout() {
    if (!this.ready) return;

    this.resetLayout();

    this.cardTop = this.getCardTop();
    this.scrollStep = this.getScrollStep();
    const runway = this.getScrollRunway();

    this.stack.style.overflow = 'visible';
    this.stack.style.position = 'relative';

    this.cards.forEach((card, index) => {
      card.classList.add('is-layout-ready');
      card.style.position = 'sticky';
      card.style.zIndex = String(10 + index);
      card.style.top = `${this.cardTop}px`;
      card.style.height = 'auto';
      card.style.paddingBottom = index < this.count - 1 ? `${runway}px` : '0';
      card.style.overflow = 'visible';
      card.style.boxSizing = 'border-box';
      card.style.transform = 'none';

      const surface = card.querySelector('.js-card-scroll__card-surface');
      if (surface) {
        surface.style.position = 'relative';
        surface.style.inset = 'auto';
        surface.style.height = 'auto';
        surface.style.overflow = 'visible';
      }
    });

    this.measureCardHeights();
    this.states = this.cards.map(() => ({ opacity: 1, translateY: 0 }));
    this.stack.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => this.layout(), { once: true });
      }
    });
    this.scheduleVisualUpdate();
  }
}

function initScrollingCardsSection(event) {
  const scope = event?.target || document;
  ScrollingCards.initAll(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ScrollingCards.initAll());
} else {
  ScrollingCards.initAll();
}

document.addEventListener('shopify:section:load', initScrollingCardsSection);
document.addEventListener('shopify:design-mode:section:load', initScrollingCardsSection);
